import { Stack } from '@mui/material'
import { useState } from 'react'

import {
  EscottMasterInput,
  MagazineItemFragment,
  Maybe,
  PaymentMethod,
  PrepaidPointsPaymentSourceFragment,
  PurchasablePrepaidPointConnectionFragment,
  PurchasablePrepaidPointDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import {
  MagazineItem,
  MagazineItemSkeleton,
  PointChargeDrawer,
  PointDisplayPurchase,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  hasNextPage: boolean
  isLoggedIn: boolean
  items: Maybe<{ edges: { node: MagazineItemFragment }[] }>
  point: number
  formattedPoint: string
  purchasablePrepaidPoints: Maybe<PurchasablePrepaidPointConnectionFragment>
  pointChargeLoading: boolean
  paymentSource: Maybe<PrepaidPointsPaymentSourceFragment>
  /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
  enablePointCarrierPayment: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onPurchaseCharge: (
    plan: PurchasablePrepaidPointDetailFragment,
    paymentMethod: Maybe<PaymentMethod>,
    escottInput: EscottMasterInput
  ) => Promise<void>
}

export const NewStoreItemsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onPurchaseCharge,
}) => {
  const { t } = useTranslation('newStoreItems')
  const { router, routerPages, routerNames, generatePath } = useRouter<{
    artistId: string
  }>()
  const [openPointPurchaseDrawer, setOpenPointPurchaseDrawer] =
    useState<boolean>(false)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.NewStoreItems} />

      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
          right: state.isLoggedIn && !!config.modules.prepaidPoint && (
            <PointDisplayPurchase
              point={state.formattedPoint}
              onClick={() => setOpenPointPurchaseDrawer(true)}
            />
          ),
        }}
      >
        <PointChargeDrawer
          point={state.point ?? 0}
          onPurchase={(plan, paymentMethod) =>
            onPurchaseCharge(plan, paymentMethod, {
              // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
              successUrl: `${window.location.origin}${generatePath(
                routerPages.NewStoreItems,
                { artistId: router.query.artistId }
              )}?points=${plan.points}`,
              // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
              cancelUrl: `${window.location.origin}${generatePath(
                routerPages.NewStoreItems,
                { artistId: router.query.artistId }
              )}`,
              // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
              errorUrl: `${window.location.origin}${generatePath(
                routerPages.NewStoreItems,
                { artistId: router.query.artistId }
              )}`,
            })
          }
          open={openPointPurchaseDrawer}
          purchasablePrepaidPoints={state.purchasablePrepaidPoints}
          onClose={() => setOpenPointPurchaseDrawer(false)}
          loading={state.loading}
          chargeLoading={state.pointChargeLoading}
          paymentSource={state.paymentSource}
          enablePointCarrierPayment={state.enablePointCarrierPayment}
          pageName="newStoreItems"
        />
        <ScrollFetcher
          nodes={state.items?.edges.map(({ node }, index) => (
            <MagazineItem
              key={node.id}
              item={node}
              divider={
                !!state.items?.edges.length &&
                index < state.items?.edges.length - 1
              }
              conductorName="newItemList"
            />
          ))}
          componentProps={{ spacing: 2 }}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('emptyMessage')}
          loadingComponent={
            <Stack spacing={2} sx={{ pt: 2 }}>
              {Array.from(Array(5).keys()).map(index => (
                <MagazineItemSkeleton key={index} divider={index < 4} />
              ))}
            </Stack>
          }
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}
